import React from 'react';
import { Card, CardContent } from '../ui/card';
import { LoadingSpinner } from './LoadingSpinner';
import { useTranslation } from '../../hooks/useTranslation';

interface LoadingIndicatorProps {
  message?: string;
  className?: string;
  fullPage?: boolean;
  showCard?: boolean;
}

/**
 * Full-page or card-based loading indicator
 * 
 * Usage:
 * - Full page: <LoadingIndicator fullPage message="Loading data..." />
 * - In card: <LoadingIndicator showCard message="Loading..." />
 * - Simple: <LoadingIndicator message="Loading..." />
 */
export const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({
  message,
  className,
  fullPage = false,
  showCard = true,
}) => {
  const { translate } = useTranslation();

  const loadingMessage = message || translate('pos.common.action.loading', 'Loading...');

  const content = (
    <div className={`flex items-center justify-center ${fullPage ? 'min-h-screen' : ''} ${className || ''}`}>
      <div className={showCard ? 'bg-white p-6 rounded-lg shadow-sm border w-full' : 'w-full'}>
        <div className="flex items-center justify-center">
          <LoadingSpinner size="lg" variant="primary" />
          {loadingMessage && (
            <span className="ml-2 text-gray-600">{loadingMessage}</span>
          )}
        </div>
      </div>
    </div>
  );

  if (fullPage) {
    return <div className="p-6 space-y-6">{content}</div>;
  }

  if (showCard) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="flex items-center">
            <LoadingSpinner size="lg" variant="primary" />
            {loadingMessage && (
              <span className="ml-2 text-muted-foreground">{loadingMessage}</span>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return content;
};
